import { useRef, useState, useEffect, useCallback } from 'react'
import { useMobile } from 'src/hooks/useMobile'
import type { MakeViewItemListEventProps } from 'src/utils/restructure/analytics'
import {
  makeViewItemListEvent,
  makeViewListImpressionEvent,
  sendEvent,
} from 'src/utils/restructure/analytics'
import { buildProductListObject } from 'src/utils/restructure/analytics/amplitude/buildProductListObject'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { InView } from 'react-intersection-observer'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import dynamicYieldEngagement from 'src/utils/dynamicYield/dynamicYieldEngagement'
import type { CountdownTheme } from 'src/components/common/Countdown/Countdown'
import CustomBox from 'src/components/common/CustomBox'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import {
  DY_ALTERNATIVE_SHELF_LAYOUT_EXPERIMENT_ID,
  SHELF_EXPERIMENT_ID,
} from 'src/components/common/StatsigExposure/StatsigExposure'
import { Statsig } from 'src/sdk/statsig'
import { HAVE_WINDOW } from 'src/constants'
import { PAGE_TYPE_MAP } from 'src/hooks/usePageType'

import ProductCard from '../../ProductCard'
import ShelfV2NavButtons from './ShelfV2NavButtons'
import { itemSizeAndQttyToScroll } from '../shelvesUtils'
import ShelfCountdown from './ShelfCountdown'
import CountdownShelfSkeleton from './CountdownShelfSkeleton'
import type { Product } from '../../types/product'

import './customSlider.css'

interface ShelfV2Props extends React.HTMLAttributes<HTMLDivElement> {
  products: Product[]
  decisionId?: string
  title: string
  engine?: string
  productListType?: string
  tabs?: Array<{ text: string; id: string }>
  activeCollection?: string
  onTabClick?: React.Dispatch<React.SetStateAction<string>>
  addCountdown?: boolean
  expireDate?: string
  theme?: CountdownTheme
  countdownId?: string
  type?: string
  id?: string
  visibilityClasses?: string
  name?: string
}

// Default expiration date for collections, defined by VTEX if not specified by us.
const defaultCollectionExpirationDate = '2073-10-08T13:44:00.000Z'

const DY_ALT_LAYOUT_DESKTOP_MAX = 5
const DY_ALT_LAYOUT_MOBILE_MAX = 4
const ALT_LAYOUT_DESKTOP_BREAKPOINT_PX = 1024
const ALT_LAYOUT_LIST_CLASS_NAME =
  'shelf-v2-alt-layout-list w-full box-border grid grid-cols-2 gap-4 overflow-hidden p-[17px] border border-[#E5E7EB] rounded-[8px] md:flex md:flex-nowrap md:gap-4 md:overflow-hidden md:p-6'

const getAlternativeLayoutProductsForTracking = (products: Product[]) => {
  const maxProducts =
    typeof window !== 'undefined' &&
    window.innerWidth < ALT_LAYOUT_DESKTOP_BREAKPOINT_PX
      ? DY_ALT_LAYOUT_MOBILE_MAX
      : DY_ALT_LAYOUT_DESKTOP_MAX

  return products.slice(0, maxProducts)
}

const ShelfV2 = ({
  products,
  title,
  decisionId,
  tabs,
  onTabClick,
  activeCollection,
  engine,
  productListType,
  addCountdown,
  expireDate,
  theme,
  countdownId,
  type,
  id,
  visibilityClasses,
  name,
  ...rest
}: ShelfV2Props) => {
  const { pageData } = useGlobalContext()
  const { isMobile, screenWidth } = useMobile()
  const listRef = useRef<HTMLUListElement | null>(null)
  const listWrapperRef = useRef<HTMLDivElement | null>(null)
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [navState, setNavState] = useState<{
    prev: boolean
    next: boolean
  }>({ prev: false, next: true })

  const [isCountdownExpired, setIsCountdownExpired] = useState<boolean>()
  const resizeObserver = useRef<ResizeObserver | null>(null)
  const scrollRafRef = useRef<number | null>(null)
  const [thumbWidth, setThumbWidth] = useState(20)
  const thumbRef = useRef<HTMLDivElement>(null)
  const isDraggingRef = useRef(false)
  const [isDragging, setIsDragging] = useState(false)
  const startXRef = useRef<number>(0)
  const initialScrollLeftRef = useRef<number>(0)
  const standardObj = getStandardObject(pageData)
  const SPEED_MULTIPLIER = -3

  const isExperimentalVitrine =
    Statsig.useStatsigFeatureGateValue(SHELF_EXPERIMENT_ID)

  const isPdpShelf = pageData.currentPageType === PAGE_TYPE_MAP.pdp

  const isAlternativeLayout = Statsig.useStatsigFeatureGateValue(
    DY_ALTERNATIVE_SHELF_LAYOUT_EXPERIMENT_ID
  )

  const isAlternativeShelfLayout = Boolean(isAlternativeLayout)
  const alternativeLayoutProducts = products.slice(
    0,
    isMobile ? DY_ALT_LAYOUT_MOBILE_MAX : DY_ALT_LAYOUT_DESKTOP_MAX
  )

  const visibleProducts = isAlternativeShelfLayout
    ? alternativeLayoutProducts
    : products

  const shelfWrapperClassName = `shelf-v2-wrapper ${
    isPdpShelf ? 'max-w-[1648px]' : 'max-w-[1280px]'
  } mx-auto my-[40px] sm:my-[80px] pl-4 sm:pl-5 md:pl-0 ${
    isAlternativeShelfLayout ? 'pr-4 sm:pr-5 md:pr-0' : ''
  }`

  const listClassName = isAlternativeShelfLayout
    ? ALT_LAYOUT_LIST_CLASS_NAME
    : `shelf-v2-carouselList flex flex-nowrap ${
        isExperimentalVitrine ? 'gap-3' : 'gap-6'
      } overflow-x-auto pb-10`

  const handleThumbMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDraggingRef.current || !thumbRef.current || !listRef.current) {
        return
      }

      const deltaX = e.clientX - startXRef.current
      const wrapper = listRef.current
      const scrollWidth = wrapper.scrollWidth - wrapper.offsetWidth

      const newScrollLeft = Math.min(
        Math.max(initialScrollLeftRef.current - deltaX * SPEED_MULTIPLIER, 0),
        scrollWidth
      )

      wrapper.scrollLeft = newScrollLeft
    },
    [listRef, SPEED_MULTIPLIER]
  )

  const handleThumbMouseUp = useCallback(() => {
    if (!thumbRef.current || !listRef.current) {
      return
    }

    isDraggingRef.current = false
    setIsDragging(false)
    document.removeEventListener('mousemove', handleThumbMouseMove)
    document.removeEventListener('mouseup', handleThumbMouseUp)
  }, [handleThumbMouseMove, listRef])

  const handleThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    if (!thumbRef.current || !listRef.current) {
      return
    }

    setIsDragging(true)
    isDraggingRef.current = true
    startXRef.current = e.clientX
    initialScrollLeftRef.current = listRef.current.scrollLeft

    document.addEventListener('mousemove', handleThumbMouseMove)
    document.addEventListener('mouseup', handleThumbMouseUp)
  }

  const onScroll = () => {
    if (scrollRafRef.current !== null) {
      return
    }

    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null

      if (!listRef?.current || !listWrapperRef?.current) {
        return
      }

      const wrapperWidth = listWrapperRef.current.getBoundingClientRect().width

      const { scrollWidth, scrollLeft } = listRef.current

      setNavState((old) => ({
        ...old,
        prev: scrollLeft !== 0,
        next: scrollWidth - wrapperWidth - scrollLeft !== 0,
      }))

      setCurrentPage(
        products.length -
          Math.round(
            (scrollWidth - scrollLeft) /
              itemSizeAndQttyToScroll(screenWidth).width
          )
      )
    })
  }

  const moveTo = (index: number) => {
    if (!listRef?.current || !listWrapperRef?.current) {
      return
    }

    listRef.current.scrollLeft =
      index * itemSizeAndQttyToScroll(screenWidth).width
  }

  const onPrevClick = (): void => {
    if (!listRef?.current || !listWrapperRef?.current) {
      return
    }

    moveTo(currentPage - itemSizeAndQttyToScroll(screenWidth).qtty)
  }

  const onNextClick = (): void => {
    if (!listRef?.current || !listWrapperRef?.current) {
      return
    }

    moveTo(currentPage + itemSizeAndQttyToScroll(screenWidth).qtty)
  }

  const handleOnViewEvents = useCallback(() => {
    const trackedProducts = isAlternativeShelfLayout
      ? getAlternativeLayoutProductsForTracking(products)
      : visibleProducts

    const items = trackedProducts.map((product: Product) => ({
      product,
      productItem: product.items[0],
      listName: `vitrine - ${title}`,
      listId: 'NA',
    }))

    const viewListImpressionEvent = makeViewListImpressionEvent(items, {
      listName: `vitrine - ${title}`,
      dimension29: engine ?? '',
    })

    const viewItemListEvent = makeViewItemListEvent(
      items as unknown as MakeViewItemListEventProps
    )

    sendEvent(viewListImpressionEvent)
    sendEvent(viewItemListEvent)

    const productListObj = buildProductListObject({
      listType: productListType ?? pageData.currentPageType,
      listName: `Vitrine: ${title}`,
      productsCount: trackedProducts.length,
    })

    dispatchAmplitudeEvent({
      eventName: 'Product List Viewed',
      eventData: {
        ...standardObj,
        ...productListObj,
      },
    })

    if (engine === 'dy' && decisionId) {
      dynamicYieldEngagement([
        {
          type: 'IMP',
          decisionId,
          variations: [Number(id)],
        },
      ])
    }
  }, [
    isAlternativeShelfLayout,
    products,
    visibleProducts,
    title,
    engine,
    productListType,
    pageData.currentPageType,
    standardObj,
    decisionId,
    id,
  ])

  const handleResize = () => {
    if (!listRef.current) {
      return
    }

    const visibleWidth = listRef.current.offsetWidth
    const contentWidth = listRef.current.scrollWidth

    setThumbWidth((visibleWidth / contentWidth) * 100)
  }

  useEffect(() => {
    if (
      !HAVE_WINDOW ||
      !listRef.current ||
      typeof window?.ResizeObserver === 'undefined'
    ) {
      return () => {}
    }

    const currentListRef = listRef.current

    if (typeof ResizeObserver === 'undefined') {
      return () => {}
    }

    handleResize()

    resizeObserver.current = new ResizeObserver(() => {
      handleResize()
    })

    resizeObserver.current?.observe(currentListRef)

    return () => {
      resizeObserver.current?.unobserve(currentListRef)
    }
  }, [])

  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleThumbMouseMove)
      document.removeEventListener('mouseup', handleThumbMouseUp)

      if (scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current)
      }
    }
  }, [handleThumbMouseMove, handleThumbMouseUp])

  useEffect(() => {
    if (!listRef.current) {
      return
    }

    const wrapper = listRef.current

    wrapper.style.scrollSnapType = isDragging ? 'none' : 'x mandatory'
    wrapper.style.scrollBehavior = isDragging ? 'unset' : 'smooth'
  }, [listRef, isDragging])

  useEffect(() => {
    if (!expireDate || expireDate === defaultCollectionExpirationDate) {
      setIsCountdownExpired(true)

      return
    }

    const unixExpireDate = new Date(expireDate).getTime()
    const unixCurrentDate = Date.now()
    const isExpired = unixExpireDate <= unixCurrentDate

    setIsCountdownExpired(isExpired)
  }, [expireDate])

  if (!visibleProducts.length && !addCountdown) {
    return null
  }

  return (
    <InView
      key={
        isAlternativeShelfLayout
          ? alternativeLayoutProducts
              .map((product) => product?.productId)
              .join('-')
          : products.map((product) => product?.productId).join('-')
      }
      as="div"
      threshold={0.7}
      onChange={(inView) => {
        if (inView) {
          handleOnViewEvents()
        }
      }}
      triggerOnce
      className={`${visibilityClasses}`}
      id={name ?? undefined}
    >
      <div
        data-testid="product-shelf"
        {...rest}
        className={shelfWrapperClassName}
      >
        <div
          className={`flex flex-wrap justify-between items-center sm:pr-5 md:pr-0 mb-8 ${
            isExperimentalVitrine ? 'sm:mb-[24.88px]' : 'sm:mb-10'
          }`}
        >
          {addCountdown ? (
            <ShelfCountdown
              isCountdownExpired={isCountdownExpired}
              setIsCountdownExpired={setIsCountdownExpired}
              expireDate={expireDate}
              theme={theme}
              countdownId={countdownId}
            />
          ) : (
            <CustomBox
              as="h2"
              className="text-2xl sm:text-[32px] font-medium font-inter"
            >
              {title}
            </CustomBox>
          )}

          {!(addCountdown && isCountdownExpired) && (
            <>
              {!isMobile &&
                !isAlternativeShelfLayout &&
                visibleProducts.length > 4 && (
                  <ShelfV2NavButtons
                    {...{ onNextClick, onPrevClick, navState }}
                  />
                )}
            </>
          )}

          {tabs?.length && tabs?.length > 1 && (
            <div
              className="w-full flex gap-2 mt-5 overflow-x-auto overflow-y-hidden whitespace-nowrap scrollbar-hide relative"
              onScroll={onScroll}
            >
              {tabs.map((tab) => {
                return (
                  tab?.text && (
                    <button
                      className={`inline-block py-2 px-3 h-[40px] rounded-round border text-sm font-normal font-inter ${
                        tab.id === activeCollection
                          ? 'border-restructure-background-action-1 text-restructure-action'
                          : 'text-tertiary border-primary  '
                      }`}
                      onClick={() => {
                        typeof onTabClick === 'function' && onTabClick(tab.id)
                        moveTo(0)
                      }}
                      key={tab.id}
                    >
                      {tab.text}
                    </button>
                  )
                )
              })}
            </div>
          )}
        </div>

        {addCountdown && isCountdownExpired ? (
          <CountdownShelfSkeleton />
        ) : (
          <div className={isAlternativeShelfLayout ? '' : 'shelf-v2-carousel'}>
            <div
              ref={listWrapperRef}
              className={isAlternativeShelfLayout ? '' : 'shelf-v2-carousel'}
            >
              <ul
                ref={listRef}
                className={`relative ${listClassName}`}
                onScroll={isAlternativeShelfLayout ? undefined : onScroll}
              >
                {visibleProducts.map((product, i) => (
                  <li
                    key={`product-$${i}`}
                    className={
                      isAlternativeShelfLayout
                        ? 'shelf-v2-alt-layout-item w-full max-w-none min-w-0 md:flex-1'
                        : `shelf-v2-carouselImageWrapper ${
                            isPdpShelf
                              ? 'max-w-[400px]'
                              : isExperimentalVitrine
                              ? 'max-w-[267px]'
                              : 'max-w-[302px]'
                          }`
                    }
                  >
                    <ProductCard
                      index={i}
                      drag={false}
                      product={{ ...product, decisionId }}
                      shelfRef={`Vitrine: ${title}`}
                      shelfEngine={engine}
                      type={productListType}
                      isSlider={!isAlternativeShelfLayout}
                      id={id}
                      title={title}
                      isPdpShelf={isPdpShelf}
                    />
                  </li>
                ))}
              </ul>
            </div>
            {!isAlternativeShelfLayout && (!isExperimentalVitrine || isMobile) && (
              <div
                className={`shelf-v2-scrollbar ${
                  thumbWidth === 100 ? 'hidden' : ''
                } `}
                style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
              >
                <div className="shelf-v2-scrollbar-track" />
                <div
                  onMouseDown={handleThumbMouseDown}
                  style={{
                    left: listRef.current
                      ? `${
                          (listRef.current.scrollLeft /
                            listRef.current.scrollWidth) *
                          100
                        }%`
                      : '0%',
                    width: `${thumbWidth}%`,
                  }}
                  ref={thumbRef}
                  className="shelf-v2-scrollbar-thumb"
                  tabIndex={0}
                  role="slider"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={
                    listRef.current
                      ? (listRef.current.scrollLeft /
                          listRef.current.scrollWidth) *
                        100
                      : 0
                  }
                  aria-label="scrollbar thumb"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </InView>
  )
}

export default ShelfV2
